/**
 * Declarative route guard backed by route-registry.
 */

import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useActiveContext } from '@/contexts/ActiveContextProvider'
import { useQAPerspective } from '@/contexts/QAPerspectiveContext'
import { getRouteById } from '@/lib/routing/route-registry'
import {
  canAccessRoute,
  getAccessDeniedRedirect,
  mapActualRoleToRouteRole,
  mapUserRoleToRouteRole,
} from '@/lib/routing/can-access-route'
import type { RouteId } from '@/lib/routing/types'
import { RouteLoading } from '@/components/routing/RouteLoading'

interface AccessRouteProps {
  routeId: RouteId
  children: ReactNode
  autoSwitchOrgContext?: boolean
}

export function AccessRoute({ routeId, children, autoSwitchOrgContext = false }: AccessRouteProps) {
  const route = getRouteById(routeId)
  const location = useLocation()
  const { accountData, isAnonymous, isLoading: authLoading, isAccountLoading, session } = useAuth()
  const {
    activeContext,
    availableContexts,
    isLoading: contextLoading,
    switchContext,
  } = useActiveContext()
  const { effectiveRole } = useQAPerspective()

  const authPending = authLoading || (!!session && isAccountLoading)
  const actualRole = mapUserRoleToRouteRole(accountData?.role)
  const effective = mapActualRoleToRouteRole(effectiveRole)
  const orgContext = availableContexts.find((context) => context.scope === 'organization')

  useEffect(() => {
    if (!autoSwitchOrgContext || authPending || contextLoading || !orgContext) return
    if (activeContext?.scope === 'organization') return
    switchContext(orgContext)
  }, [
    autoSwitchOrgContext,
    authPending,
    contextLoading,
    orgContext,
    activeContext?.scope,
    switchContext,
  ])

  if (!route) {
    console.error(`[AccessRoute] Unknown route id: ${routeId}`)
    return <Navigate to="/" replace />
  }

  if (authPending || contextLoading) {
    return <RouteLoading />
  }

  if (autoSwitchOrgContext && orgContext && activeContext?.scope !== 'organization') {
    return <RouteLoading />
  }

  const allowed = canAccessRoute({
    route,
    actualRole,
    effectiveRole: effective,
    activeContextScope: activeContext?.scope ?? null,
    isAnonymous,
  })

  if (!allowed) {
    const redirectTo = getAccessDeniedRedirect(effective)
    return <Navigate to={redirectTo} replace state={{ from: location }} />
  }

  return <>{children}</>
}
